import type { ChatMessage } from '@mg/shared';

/**
 * Which message's option buttons are live, and which question an answer belongs to.
 *
 * Buttons used to be live on the very last message only. That is right once a question has been
 * answered — an old "Create Draft" must not wake up ten turns later — and wrong when the turn that
 * was meant to answer it came to nothing: the stock "Sorry … please try again" bubble became the
 * last message, the question's buttons vanished, and "try again" could only be done by typing.
 *
 * The client cannot tell that apology from an ordinary plain-text reply, so the server marks it
 * (`data.kind === 'turn-failed'`) and a marked reply is simply not counted here. A question
 * therefore stays answerable until a turn after it SUCCEEDS; answered questions stay inert.
 *
 * One state looks like a turn that came to nothing and may not be: the thread ENDS with the user's
 * own message. The server stores that message before it runs the model and the reply only once the
 * turn is done, so for the whole length of a turn this is what every other view of the thread
 * reads — a reloaded page, a second tab, the page left and come back to. Buttons offered there
 * start a second turn beside the first, and the drafts can be saved twice. So an unanswered message
 * keeps every button asleep until it is old enough that no turn can still be running.
 */

/**
 * How long after a user's message the turn answering it may still be running on the server.
 *
 * The server sets no limit of its own on a turn: it is up to three model calls and a save. The
 * request carrying it is cut by the ingress at four minutes but the handler carries on, and a model
 * call that hangs is only given up on — and tried again — after the SDK's ten minutes. Fifteen
 * covers one such call plus a whole healthy turn; it is a judgement, not a hard ceiling. Erring
 * long costs little: until then the question is answered by typing, as it was before the buttons
 * could come back at all. Erring short costs a second set of drafts.
 */
export const TURN_MAY_STILL_RUN_MS = 15 * 60_000;

/** A reply that says something — not the marker of a turn that came to nothing. */
const isRealReply = (m: ChatMessage): boolean =>
  m.role === 'assistant' && m.data?.kind !== 'turn-failed';

/**
 * Whether the thread ends with a user's message whose turn may still be running: nothing has
 * answered it, and it is no older than TURN_MAY_STILL_RUN_MS. A date that cannot be read counts as
 * recent — the buttons are never offered on a guess.
 */
function awaitsItsReply(messages: readonly ChatMessage[], now: number): boolean {
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'user') return false;
  return !(now - Date.parse(last.createdAt) > TURN_MAY_STILL_RUN_MS);
}

/**
 * Index of the message whose option buttons (if it has any) are live: the last real reply.
 * -1 when the thread has none, or while its last message may still be getting its reply.
 *
 * @param now The time to measure the last message's age against; the page leaves it to the clock.
 */
export function liveChoiceIndex(
  messages: readonly ChatMessage[],
  now: number = Date.now(),
): number {
  if (awaitsItsReply(messages, now)) return -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (isRealReply(messages[i])) return i;
  }
  return -1;
}

/**
 * The question message `index` may be answering: the nearest real reply before it.
 *
 * Usually that is just the message before. It is not after a failed turn — an answer clicked again
 * sits after the apology, not after its question — and the question is what carries the button
 * labels (see userMessageText).
 */
export function questionFor(messages: readonly ChatMessage[], index: number): ChatMessage | null {
  for (let i = index - 1; i >= 0; i -= 1) {
    if (isRealReply(messages[i])) return messages[i];
  }
  return null;
}
